import fcose from 'cytoscape-fcose'

/**
 * ASEZAREA hartii ADM - unde sta fiecare nod.
 *
 * Plangerea de la care a pornit: pe vederea „communication" totul se suprapunea.
 * `cose`, cum era pus, NU promite nimic despre neacoperire - respinge nodurile
 * unul de altul cat poate in numarul lui de pasi si se opreste unde a ajuns, cu
 * etichetele (care sunt mai late decat nodul) peste vecini.
 *
 * DE-AIA ORICE ASEZARE SE TERMINA CU ACELASI PAS: `removeOverlaps()` primeste
 * dreptunghiul fiecarui nod CU eticheta lui si il impinge pana nu mai atinge
 * nimic. Pasul e scris sa garanteze, nu sa incerce: dupa impingerile obisnuite
 * urmeaza o trecere de la stanga la dreapta care pune fiecare nod la dreapta
 * oricui il mai atinge - si aceea se termina mereu, fara acoperire.
 *
 * CE SE POATE ALEGE (`Arrange`, valorile se salveaza in vederi ca text - nu se
 * redenumesc):
 *
 *   auto        ce era pe fiecare vedere (ierarhie pe infrastructura si
 *               topologie, forta pe comunicatie), dar fara suprapuneri;
 *   tiers       benzi orizontale fixe, de la Internet la statii - nivelul se
 *               citeste din clasa, subtip, roluri, rolul in aplicatie si
 *               porturile pe care asculta nodul (`tierOf`);
 *   force       fCoSE (cytoscape-fcose, MIT), cu dimensiunile etichetelor,
 *               apoi componentele asezate una langa alta si pasul final;
 *   hierarchy   pe randuri, dupa distanta de la nodul de pornire;
 *   grid        grupat pe clasa, cate un bloc pe clasa;
 *   concentric  inele, in mijloc ce conteaza mai mult (importanta + legaturi).
 *
 * Toate, in afara de `force`, se calculeaza AICI, in functii pure, si ajung la
 * cytoscape ca pozitii gata facute (`preset`). Asa se pot proba fara panza, si
 * acelasi graf da mereu acelasi desen.
 */

export const ARRANGES = ['auto', 'tiers', 'force', 'hierarchy', 'grid', 'concentric'] as const

export type Arrange = (typeof ARRANGES)[number]

export const ARRANGE_LABELS: Record<Arrange, string> = {
  auto: 'Auto',
  tiers: 'Tiers',
  force: 'Force (no overlap)',
  hierarchy: 'Hierarchy',
  grid: 'Grid (by type)',
  concentric: 'Concentric',
}

export function isArrange(value: unknown): value is Arrange {
  return typeof value === 'string' && (ARRANGES as readonly string[]).includes(value)
}

/* ------------------------------------------------------------------------ */
/* Nivelurile                                                                */
/* ------------------------------------------------------------------------ */

export type TierKey =
  | 'services'
  | 'perimeter'
  | 'network'
  | 'front'
  | 'application'
  | 'data'
  | 'infrastructure'
  | 'endpoints'
  | 'other'

/**
 * Benzile, de sus in jos. Serviciile (de afaceri si IT) sunt lucruri logice,
 * nu aparate, si stau deasupra a tot: de ele atarna restul. „Unclassified" e
 * ultima, ca un aparat despre care nu se stie nimic sa nu se dea drept statie.
 */
export const TIERS: { key: TierKey; label: string }[] = [
  { key: 'services', label: 'Services' },
  { key: 'perimeter', label: 'Internet / perimeter' },
  { key: 'network', label: 'Network' },
  { key: 'front', label: 'Front end' },
  { key: 'application', label: 'Application' },
  { key: 'data', label: 'Data' },
  { key: 'infrastructure', label: 'Infrastructure' },
  { key: 'endpoints', label: 'Workstations & peripherals' },
  { key: 'other', label: 'Unclassified' },
]

const TIER_INDEX: Record<TierKey, number> = Object.fromEntries(TIERS.map((t, i) => [t.key, i])) as Record<TierKey, number>

/** Ce stie asezarea despre un nod - exact campurile din `/adm/map/data`. */
export interface TierNode {
  id: number | string
  label?: string
  class?: string
  kind?: number
  subtype?: string
  roles?: string[]
  /** `it_business_app_components.role`: 1 baza de date, 2 aplicatie, 3 web, 4 statie client. */
  appRole?: number
  criticality?: number | null
}

export interface TierEdge {
  source: number | string
  target: number | string
  type?: string
  port?: string
  label?: string
}

/** `it_business_app_components.role` -> banda. */
const APP_ROLE_TIER: Record<number, TierKey> = { 1: 'data', 2: 'application', 3: 'front', 4: 'endpoints' }

/**
 * Rolurile din `Cmdb\Taxonomy::roles()`. Cand un aparat are mai multe, castiga
 * cel mai de jos din stiva (`ROLE_PRIORITY`): un server de fisiere care e si
 * gazda de virtualizare e intai gazda.
 */
const ROLE_TIER: Record<string, TierKey> = {
  edge_firewall: 'perimeter',
  core_switch: 'network',
  distribution_switch: 'network',
  access_switch: 'network',
  wifi_controller: 'network',
  web_server: 'front',
  proxy_server: 'front',
  database_server: 'data',
  backup_server: 'data',
  storage_server: 'data',
  file_server: 'data',
  virtualization_host: 'infrastructure',
  container_host: 'infrastructure',
  application_server: 'application',
  mail_server: 'application',
  domain_controller: 'application',
  dns_server: 'application',
  dhcp_server: 'application',
  print_server: 'application',
  monitoring_server: 'application',
  authentication_server: 'application',
  cicd_server: 'application',
  management_server: 'application',
  security_server: 'application',
  terminal_server: 'application',
  vdi_server: 'application',
}

const ROLE_PRIORITY: TierKey[] = ['perimeter', 'network', 'infrastructure', 'data', 'front', 'application']

/**
 * Clasa si subtipul, in ordinea in care se intreaba. ORDINEA CONTEAZA:
 * „IoT Gateway" e un aparat IoT, nu o poarta spre Internet, deci IoT se
 * intreaba inaintea lui „gateway"; un „IP phone" e o statie, nu retea.
 */
const TEXT_RULES: [RegExp, TierKey][] = [
  [/internet domain|\bdomain\b/, 'perimeter'],
  [/\biot\b|camera|sensor|\bplc\b|\bhmi\b|access control|ip phone|video conference|room system/, 'endpoints'],
  [/load balancer|\bproxy\b|\bcdn\b|certificate|web server|\bweb\b/, 'front'],
  [/firewall|\bvpn\b|sd-?wan|router|gateway|\bwaf\b|ids\/ips|\butm\b|egress|internet/, 'perimeter'],
  [/switch|access point|wireless|wi-?fi|\bwlan\b|network|\bsbc\b|\bpbx\b|telecom/, 'network'],
  [/\bsan\b|\bnas\b|storage|tape|backup|database|\bdb\b|sql|oracle|postgres|mongo|redis|elastic/, 'data'],
  [/hypervisor|esxi|hyper-v|proxmox|nutanix|virtuali[sz]ation host|chassis|blade|\bups\b|\bpdu\b|\brack\b|power|environmental/, 'infrastructure'],
  [/desktop|laptop|workstation|thin client|kiosk|\bpos\b|printer|\bmfp\b|scanner|plotter|mobile|smartphone|tablet|phone|handheld|rugged|endpoint|media player/, 'endpoints'],
]

/** Un server fara alt semn: ruleaza ceva, deci aplicatie - sau gazda, daca ruleaza ceva PE el. */
const GENERIC_SERVER = /server|virtual|compute|instance|\bvm\b|container|cloud/

/** Porturi pe care asculta o baza de date sau o stocare. 445 (SMB) lipseste: il are orice Windows. */
const DATA_PORTS = new Set([1433, 1521, 3306, 5432, 5984, 6379, 7000, 9042, 9200, 9300, 11211, 27017, 2049, 3260])

/** Porturi de web si de proxy. */
const FRONT_PORTS = new Set([80, 443, 8000, 8008, 8080, 8443, 8888, 3128])

/** Ce se afla din legaturi, nu din nod: pe ce porturi e chemat, si daca ruleaza ceva pe el. */
export interface GraphHints {
  ports: Map<string, Set<number>>
  hosts: Set<string>
}

/** „HTTPS (443)", „~443", „mysqld (3306)" -> 443 / 3306. */
export function portNumber(text: string | undefined): number | null {
  if (!text) return null
  const match = /(\d{1,5})\)?\s*$/.exec(text.trim())
  if (!match) return null
  const port = Number(match[1])
  return port > 0 && port < 65536 ? port : null
}

export function graphHints(nodes: TierNode[], edges: TierEdge[]): GraphHints {
  const kind = new Map(nodes.map((n) => [String(n.id), Number(n.kind ?? 0)]))
  const ports = new Map<string, Set<number>>()
  const hosts = new Set<string>()

  for (const edge of edges) {
    const source = String(edge.source)
    const target = String(edge.target)

    if (edge.type === 'connects_to') {
      const port = portNumber(edge.port ?? edge.label)
      if (port !== null) {
        if (!ports.has(target)) ports.set(target, new Set())
        ports.get(target)!.add(port)
      }
    } else if (edge.type === 'runs_on') {
      /* „X runs on Y": Y e gazda. */
      hosts.add(target)
    } else if (edge.type === 'contains' && kind.get(source) === 1) {
      /* Un aparat care contine alte lucruri: sasiu, rack, gazda. */
      hosts.add(source)
    }
  }

  return { ports, hosts }
}

/**
 * NIVELUL UNUI NOD. Intrebarile, de la cea mai sigura la cea mai ghicita:
 *
 *  1. felul: serviciile (de afaceri, IT) sunt banda lor;
 *  2. rolul intr-o aplicatie de business - l-a pus un om, pe aplicatia aia;
 *  3. rolurile aparatului (`database_server`, `virtualization_host`...);
 *  4. clasa si subtipul (Firewall, Switch, NAS, Laptop...);
 *  5. porturile pe care e chemat (3306 -> date, 443 -> front);
 *  6. daca ruleaza ceva pe el -> infrastructura;
 *  7. o aplicatie, sau un server fara alt semn -> aplicatie;
 *  8. altfel: „Unclassified", nu o banda aleasa la intamplare.
 *
 * Numele nodului NU se citeste: „SRV-DB" pare baza de date, dar „DB" poate fi
 * la fel de bine „Dublin". Un nume e ales de om pentru om, nu pentru desen.
 */
export function tierOf(node: TierNode, hints?: GraphHints): TierKey {
  const kind = Number(node.kind ?? 0)
  if (kind === 3 || kind === 4) return 'services'

  const fromApp = APP_ROLE_TIER[Number(node.appRole ?? 0)]
  if (fromApp) return fromApp

  const roleTiers = new Set((node.roles ?? []).map((r) => ROLE_TIER[r]).filter((t): t is TierKey => Boolean(t)))
  for (const tier of ROLE_PRIORITY) {
    if (roleTiers.has(tier)) return tier
  }

  const text = `${node.class ?? ''} ${node.subtype ?? ''}`.toLowerCase()
  for (const [pattern, tier] of TEXT_RULES) {
    if (pattern.test(text)) return tier
  }

  const id = String(node.id)
  const ports = hints?.ports.get(id)
  if (ports) {
    for (const p of ports) if (DATA_PORTS.has(p)) return 'data'
    for (const p of ports) if (FRONT_PORTS.has(p)) return 'front'
  }

  if (hints?.hosts.has(id)) return 'infrastructure'

  if (kind === 2) return 'application'
  if (GENERIC_SERVER.test(text)) return 'application'

  return 'other'
}

/** Banda fiecarui nod, cu legaturile citite o singura data. */
export function assignTiers(nodes: TierNode[], edges: TierEdge[]): Map<string, TierKey> {
  const hints = graphHints(nodes, edges)
  return new Map(nodes.map((n) => [String(n.id), tierOf(n, hints)]))
}

/* ------------------------------------------------------------------------ */
/* Geometria                                                                 */
/* ------------------------------------------------------------------------ */

export interface Size {
  w: number
  h: number
}

/** Centrul dreptunghiului (nod + eticheta). */
export interface Rect extends Size {
  id: string
  x: number
  y: number
}

/** Banda sau blocul de grup - dreptunghi desenat sub noduri, cu numele lui. */
export interface Decoration {
  id: string
  label: string
  kind: 'band' | 'group'
  x: number
  y: number
  w: number
  h: number
}

export interface Arrangement {
  positions: Map<string, { x: number; y: number }>
  decorations: Decoration[]
}

/** Spatiul minim intre doua dreptunghiuri (care includ deja eticheta). */
export const GAP = 14

/**
 * Cat ocupa un nod CU eticheta, cand nu se poate masura pe panza (probe).
 * Pe panza se masoara cu `boundingBox({ includeLabels: true })`.
 */
export function estimateSize(label: string, root = false): Size {
  const node = root ? 42 : 30
  const text = Math.ceil(label.length * 6.3) + 8
  return { w: Math.max(node + 6, text), h: node + 6 + 19 }
}

export function overlapping(a: Rect, b: Rect, gap = 0): boolean {
  return Math.abs(a.x - b.x) < (a.w + b.w) / 2 + gap - 1e-6 && Math.abs(a.y - b.y) < (a.h + b.h) / 2 + gap - 1e-6
}

/** Toate perechile care se ating - pentru probe si pentru decizia de a mai impinge. */
export function findOverlaps(rects: Rect[], gap = 0): [string, string][] {
  const out: [string, string][] = []
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (overlapping(rects[i], rects[j], gap)) out.push([rects[i].id, rects[j].id])
    }
  }
  return out
}

/**
 * NEACOPERIREA, GARANTAT.
 *
 * 1. Impingeri: fiecare pereche care se atinge se desparte pe axa pe care
 *    acoperirea e mai mica, jumatate fiecare. De obicei asta e tot - si
 *    misca putin, deci forma asezarii ramane.
 * 2. Daca dupa `rounds` ture tot mai e ceva, trecerea de la stanga la dreapta:
 *    nodurile luate in ordinea lui x, si fiecare dus la dreapta oricarui nod
 *    deja asezat pe care il atinge. Un nod asezat nu se mai misca, iar cel
 *    curent doar spre dreapta - deci fiecare vecin il poate impinge cel mult o
 *    data, si la sfarsit nu mai atinge nimic. Asta e garantia; prima faza e
 *    doar ca rezultatul sa arate bine.
 */
export function removeOverlaps(input: Rect[], gap = GAP, rounds = 80): Rect[] {
  const rects = input.map((r) => ({ ...r }))
  if (rects.length < 2) return rects

  const cell = Math.max(...rects.map((r) => Math.max(r.w, r.h))) + gap

  for (let round = 0; round < rounds; round++) {
    /* Grila de vecinatate: o pereche se poate atinge numai in celule alaturate. */
    const grid = new Map<string, number[]>()
    rects.forEach((r, i) => {
      const key = `${Math.floor(r.x / cell)}:${Math.floor(r.y / cell)}`
      if (!grid.has(key)) grid.set(key, [])
      grid.get(key)!.push(i)
    })

    let moved = false

    rects.forEach((a, i) => {
      const cx = Math.floor(a.x / cell)
      const cy = Math.floor(a.y / cell)
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (const j of grid.get(`${cx + dx}:${cy + dy}`) ?? []) {
            if (j <= i) continue
            const b = rects[j]
            const ox = (a.w + b.w) / 2 + gap - Math.abs(a.x - b.x)
            const oy = (a.h + b.h) / 2 + gap - Math.abs(a.y - b.y)
            if (ox <= 0 || oy <= 0) continue
            moved = true
            if (ox < oy) {
              const sign = a.x < b.x || (a.x === b.x && i < j) ? -1 : 1
              a.x += (sign * ox) / 2 + sign * 0.01
              b.x -= (sign * ox) / 2 + sign * 0.01
            } else {
              const sign = a.y < b.y || (a.y === b.y && i < j) ? -1 : 1
              a.y += (sign * oy) / 2 + sign * 0.01
              b.y -= (sign * oy) / 2 + sign * 0.01
            }
          }
        }
      }
    })

    if (!moved) return rects
  }

  /* Faza a doua - garantia. */
  const order = rects.map((_, i) => i).sort((p, q) => rects[p].x - rects[q].x || p - q)
  const placed: Rect[] = []
  for (const i of order) {
    const r = rects[i]
    for (;;) {
      const hit = placed.find((p) => overlapping(p, r, gap))
      if (!hit) break
      r.x = hit.x + (hit.w + r.w) / 2 + gap + 0.01
    }
    placed.push(r)
  }
  return rects
}

/**
 * COMPONENTELE NELEGATE, una langa alta, pe randuri.
 *
 * O asezare de forta pune insulele unde se nimeresc - de multe ori una peste
 * alta, sau departe intr-un colt. Aici fiecare insula isi pastreaza forma si
 * se muta intreaga, cea mai mare prima.
 */
export function packComponents(rects: Rect[], edges: TierEdge[], gap = 60): Rect[] {
  const index = new Map(rects.map((r, i) => [r.id, i]))
  const parent = rects.map((_, i) => i)
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])))
  for (const e of edges) {
    const a = index.get(String(e.source))
    const b = index.get(String(e.target))
    if (a !== undefined && b !== undefined) parent[find(a)] = find(b)
  }

  const groups = new Map<number, Rect[]>()
  rects.forEach((r, i) => {
    const root = find(i)
    if (!groups.has(root)) groups.set(root, [])
    groups.get(root)!.push({ ...r })
  })

  const comps = [...groups.values()].map((members) => {
    const left = Math.min(...members.map((r) => r.x - r.w / 2))
    const right = Math.max(...members.map((r) => r.x + r.w / 2))
    const top = Math.min(...members.map((r) => r.y - r.h / 2))
    const bottom = Math.max(...members.map((r) => r.y + r.h / 2))
    return { members, left, top, w: right - left, h: bottom - top }
  })
  comps.sort((a, b) => b.w * b.h - a.w * a.h || b.members.length - a.members.length)

  const area = comps.reduce((s, c) => s + (c.w + gap) * (c.h + gap), 0)
  const maxWidth = Math.max(comps[0]?.w ?? 0, Math.sqrt(area) * 1.4)

  let x = 0
  let y = 0
  let rowHeight = 0
  const out: Rect[] = []
  for (const c of comps) {
    if (x > 0 && x + c.w > maxWidth) {
      x = 0
      y += rowHeight + gap
      rowHeight = 0
    }
    for (const r of c.members) out.push({ ...r, x: r.x - c.left + x, y: r.y - c.top + y })
    x += c.w + gap
    rowHeight = Math.max(rowHeight, c.h)
  }
  return out
}

/* ------------------------------------------------------------------------ */
/* Randuri: benzile si ierarhia                                              */
/* ------------------------------------------------------------------------ */

function adjacency(edges: TierEdge[], ids: Set<string>): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>()
  for (const id of ids) adj.set(id, new Set())
  for (const e of edges) {
    const s = String(e.source)
    const t = String(e.target)
    if (s === t || !ids.has(s) || !ids.has(t)) continue
    adj.get(s)!.add(t)
    adj.get(t)!.add(s)
  }
  return adj
}

/**
 * ORDINEA IN RAND: fiecare nod se muta spre media vecinilor lui din celelalte
 * randuri (baricentrul), cateva treceri in jos si in sus. Legaturile ies mai
 * scurte si se incruciseaza mai putin decat in ordinea alfabetica.
 */
function orderLevels(levels: string[][], adj: Map<string, Set<string>>): string[][] {
  const out = levels.map((l) => [...l])
  const levelOf = new Map<string, number>()
  out.forEach((l, i) => l.forEach((id) => levelOf.set(id, i)))
  const pos = new Map<string, number>()
  const setPos = (l: string[]) => l.forEach((id, i) => pos.set(id, l.length === 1 ? 0.5 : i / (l.length - 1)))
  out.forEach(setPos)

  const reorder = (i: number) => {
    const keyed = out[i].map((id, at) => {
      const nb = [...(adj.get(id) ?? [])].filter((n) => levelOf.get(n) !== i)
      const bc = nb.length > 0 ? nb.reduce((s, n) => s + (pos.get(n) ?? 0.5), 0) / nb.length : pos.get(id)!
      return { id, bc, at }
    })
    keyed.sort((a, b) => a.bc - b.bc || a.at - b.at)
    out[i] = keyed.map((k) => k.id)
    setPos(out[i])
  }

  for (let pass = 0; pass < 3; pass++) {
    if (pass % 2 === 0) for (let i = 0; i < out.length; i++) reorder(i)
    else for (let i = out.length - 1; i >= 0; i--) reorder(i)
  }
  return out
}

export interface RowsResult {
  positions: Map<string, { x: number; y: number }>
  /** Marginile fiecarui rand (banda), in ordinea primita; `null` pentru un rand gol. */
  bands: ({ top: number; bottom: number } | null)[]
  left: number
  right: number
}

/**
 * Randurile, de sus in jos, fiecare centrat pe x = 0. Un rand prea lung se
 * rupe in mai multe sub-randuri in aceeasi banda, ca o banda cu trei sute de
 * statii sa nu iasa de zece ori mai lata decat ecranul.
 */
export function placeRows(
  levels: string[][],
  sizes: Map<string, Size>,
  edges: TierEdge[],
  options: { gapX?: number; gapY?: number; bandGap?: number } = {},
): RowsResult {
  const gapX = options.gapX ?? 22
  const gapY = options.gapY ?? 26
  const bandGap = options.bandGap ?? 70

  const ids = new Set(levels.flat())
  const ordered = orderLevels(levels, adjacency(edges, ids))

  const area = [...ids].reduce((s, id) => {
    const z = sizes.get(id)!
    return s + (z.w + gapX) * (z.h + gapY)
  }, 0)
  const maxRow = Math.min(6000, Math.max(1100, Math.sqrt(area) * 2))

  const positions = new Map<string, { x: number; y: number }>()
  const bands: ({ top: number; bottom: number } | null)[] = []
  let y = 0
  let left = 0
  let right = 0

  for (const level of ordered) {
    if (level.length === 0) {
      bands.push(null)
      continue
    }

    /* Ruperea in sub-randuri, pastrand ordinea. */
    const rows: string[][] = [[]]
    let width = 0
    for (const id of level) {
      const w = sizes.get(id)!.w
      if (rows[rows.length - 1].length > 0 && width + w > maxRow) {
        rows.push([])
        width = 0
      }
      rows[rows.length - 1].push(id)
      width += w + gapX
    }

    const top = y
    rows.forEach((row, r) => {
      const height = Math.max(...row.map((id) => sizes.get(id)!.h))
      const total = row.reduce((s, id) => s + sizes.get(id)!.w, 0) + gapX * (row.length - 1)
      let x = -total / 2
      for (const id of row) {
        const w = sizes.get(id)!.w
        positions.set(id, { x: x + w / 2, y: y + height / 2 })
        x += w + gapX
      }
      left = Math.min(left, -total / 2)
      right = Math.max(right, total / 2)
      y += height + (r < rows.length - 1 ? gapY : 0)
    })
    bands.push({ top, bottom: y })
    y += bandGap
  }

  return { positions, bands, left, right }
}

/** Ordinea de pornire intr-un rand: dupa clasa, apoi dupa nume - ca la egalitate sa iasa la fel. */
function byClassThenLabel(nodes: Map<string, TierNode>) {
  return (a: string, b: string) => {
    const na = nodes.get(a)
    const nb = nodes.get(b)
    return (
      (na?.class ?? '').localeCompare(nb?.class ?? '') || (na?.label ?? '').localeCompare(nb?.label ?? '') || a.localeCompare(b)
    )
  }
}

/** Asezarea pe niveluri, cu banda si numele ei pentru fiecare nivel care are noduri. */
export function tiersArrangement(nodes: TierNode[], edges: TierEdge[], sizes: Map<string, Size>): Arrangement {
  const tiers = assignTiers(nodes, edges)
  const byId = new Map(nodes.map((n) => [String(n.id), n]))
  const levels: string[][] = TIERS.map(() => [])
  for (const n of nodes) levels[TIER_INDEX[tiers.get(String(n.id))!]].push(String(n.id))
  levels.forEach((l) => l.sort(byClassThenLabel(byId)))

  const rows = placeRows(levels, sizes, edges)
  const pad = 16
  const decorations: Decoration[] = []
  rows.bands.forEach((band, i) => {
    if (!band) return
    decorations.push({
      id: `__arrange_band_${TIERS[i].key}`,
      label: TIERS[i].label,
      kind: 'band',
      x: (rows.left + rows.right) / 2,
      y: (band.top + band.bottom) / 2,
      w: rows.right - rows.left + pad * 2,
      h: band.bottom - band.top + pad * 2,
    })
  })

  return { positions: rows.positions, decorations }
}

/**
 * Randurile dupa distanta de la nodul de pornire.
 *
 * `directed`: ca `breadthfirst` cu `directed: true`, de pe infrastructura -
 * pornesc nodurile in care nu intra nimic, si se merge numai pe sensul
 * sagetii. Fara sens, fiecare insula porneste de la nodul ales (radacina
 * hartii) sau, daca nu e in ea, de la nodul ei cel mai legat.
 */
export function hierarchyArrangement(
  nodes: TierNode[],
  edges: TierEdge[],
  sizes: Map<string, Size>,
  root: string | number | null,
  directed = false,
): Arrangement {
  const ids = nodes.map((n) => String(n.id))
  const idSet = new Set(ids)
  const byId = new Map(nodes.map((n) => [String(n.id), n]))
  const adj = adjacency(edges, idSet)
  const out = new Map<string, Set<string>>(ids.map((id) => [id, new Set()]))
  const indeg = new Map<string, number>(ids.map((id) => [id, 0]))
  for (const e of edges) {
    const s = String(e.source)
    const t = String(e.target)
    if (s === t || !idSet.has(s) || !idSet.has(t) || out.get(s)!.has(t)) continue
    out.get(s)!.add(t)
    indeg.set(t, indeg.get(t)! + 1)
  }

  const degree = (id: string) => adj.get(id)!.size
  const level = new Map<string, number>()
  const walk = (starts: string[]) => {
    const queue = [...starts]
    starts.forEach((s) => level.set(s, 0))
    while (queue.length > 0) {
      const id = queue.shift()!
      const next = directed ? out.get(id)! : adj.get(id)!
      for (const n of next) {
        if (level.has(n)) continue
        level.set(n, level.get(id)! + 1)
        queue.push(n)
      }
    }
  }

  const rootId = root === null || root === undefined ? '' : String(root)
  if (rootId !== '' && rootId !== '0' && idSet.has(rootId)) walk([rootId])

  if (directed) {
    const sources = ids.filter((id) => !level.has(id) && indeg.get(id) === 0)
    if (sources.length > 0) walk(sources)
  }

  /* Ce a ramas (alte insule, cicluri): cate o pornire pe insula, din nodul cel mai legat. */
  for (;;) {
    const rest = ids.filter((id) => !level.has(id))
    if (rest.length === 0) break
    rest.sort((a, b) => degree(b) - degree(a) || a.localeCompare(b))
    walk([rest[0]])
  }

  const depth = Math.max(0, ...level.values())
  const levels: string[][] = Array.from({ length: depth + 1 }, () => [])
  for (const id of ids) levels[level.get(id)!].push(id)
  levels.forEach((l) => l.sort(byClassThenLabel(byId)))

  return { positions: placeRows(levels, sizes, edges, { bandGap: 60 }).positions, decorations: [] }
}

/* ------------------------------------------------------------------------ */
/* Grila pe clase si inelele                                                 */
/* ------------------------------------------------------------------------ */

const KIND_LABEL: Record<number, string> = { 1: 'Device', 2: 'Application', 3: 'IT service', 4: 'Business service' }

/** Grupat pe clasa: cate un bloc pe clasa, blocurile in ordinea benzilor, apoi dupa nume. */
export function gridArrangement(nodes: TierNode[], edges: TierEdge[], sizes: Map<string, Size>): Arrangement {
  const tiers = assignTiers(nodes, edges)
  const groups = new Map<string, TierNode[]>()
  for (const n of nodes) {
    const key = n.class && n.class !== '' ? n.class : (KIND_LABEL[Number(n.kind ?? 0)] ?? 'Other')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(n)
  }

  const header = 24
  const gap = 46
  const blocks = [...groups.entries()]
    .map(([name, members]) => {
      members.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '') || String(a.id).localeCompare(String(b.id)))
      const tier = Math.min(...members.map((m) => TIER_INDEX[tiers.get(String(m.id))!]))
      const cellW = Math.max(...members.map((m) => sizes.get(String(m.id))!.w)) + GAP + 4
      const cellH = Math.max(...members.map((m) => sizes.get(String(m.id))!.h)) + GAP
      const cols = Math.max(1, Math.ceil(Math.sqrt((members.length * cellH) / cellW) * 1.3))
      const rows = Math.ceil(members.length / cols)
      return { name, members, tier, cellW, cellH, cols: Math.min(cols, members.length), rows }
    })
    .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name))

  const area = blocks.reduce((s, b) => s + (b.cols * b.cellW + gap) * (b.rows * b.cellH + header + gap), 0)
  const maxWidth = Math.max(...blocks.map((b) => b.cols * b.cellW), Math.sqrt(area) * 1.5)

  const positions = new Map<string, { x: number; y: number }>()
  const decorations: Decoration[] = []
  let x = 0
  let y = 0
  let rowHeight = 0
  blocks.forEach((b, bi) => {
    const w = b.cols * b.cellW
    const h = b.rows * b.cellH
    if (x > 0 && x + w > maxWidth) {
      x = 0
      y += rowHeight + gap
      rowHeight = 0
    }
    const top = y + header
    b.members.forEach((m, i) => {
      const col = i % b.cols
      const row = Math.floor(i / b.cols)
      positions.set(String(m.id), { x: x + col * b.cellW + b.cellW / 2, y: top + row * b.cellH + b.cellH / 2 })
    })
    decorations.push({
      id: `__arrange_group_${bi}`,
      label: `${b.name} (${b.members.length})`,
      kind: 'group',
      x: x + w / 2,
      y: top + h / 2,
      w: w + 8,
      h: h + 8,
    })
    x += w + gap
    rowHeight = Math.max(rowHeight, h + header)
  })

  return { positions, decorations }
}

/**
 * Inele: in mijloc nodul de pornire, apoi dupa importanta si numarul de
 * legaturi. Fiecare inel ia atatea noduri cate incap pe circumferinta lui,
 * socotind diagonala dreptunghiului - deci nici in varful cercului, nici pe
 * laturi nu se ating.
 */
export function concentricArrangement(
  nodes: TierNode[],
  edges: TierEdge[],
  sizes: Map<string, Size>,
  root: string | number | null,
): Arrangement {
  const ids = nodes.map((n) => String(n.id))
  const adj = adjacency(edges, new Set(ids))
  const byId = new Map(nodes.map((n) => [String(n.id), n]))
  const rootId = root === null || root === undefined ? '' : String(root)
  const score = (id: string) =>
    (id === rootId ? 1e9 : 0) + adj.get(id)!.size * 3 + Number(byId.get(id)?.criticality ?? 0) * 2
  const sorted = [...ids].sort((a, b) => score(b) - score(a) || (byId.get(a)?.label ?? '').localeCompare(byId.get(b)?.label ?? '') || a.localeCompare(b))

  const diag = (id: string) => {
    const z = sizes.get(id)!
    return Math.hypot(z.w, z.h)
  }

  const positions = new Map<string, { x: number; y: number }>()
  if (sorted.length === 0) return { positions, decorations: [] }

  positions.set(sorted[0], { x: 0, y: 0 })
  let radius = 0
  let prevExtent = diag(sorted[0]) / 2
  let at = 1

  while (at < sorted.length) {
    /* Inelul urmator: cat de departe, si cine incape pe el. */
    let ring: string[] = []
    let ringExtent = 0
    let r = radius
    for (;;) {
      const candidate = sorted[at + ring.length]
      if (candidate === undefined) break
      const extent = Math.max(ringExtent, diag(candidate) / 2)
      const rr = radius + prevExtent + extent + GAP
      const need = [...ring, candidate].reduce((s, id) => s + diag(id) + GAP, 0)
      if (ring.length > 0 && need > 2 * Math.PI * rr) break
      ring = [...ring, candidate]
      ringExtent = extent
      r = rr
    }

    const total = ring.reduce((s, id) => s + diag(id) + GAP, 0)
    let angle = -Math.PI / 2
    for (const id of ring) {
      const share = ((diag(id) + GAP) / total) * 2 * Math.PI
      positions.set(id, { x: r * Math.cos(angle + share / 2), y: r * Math.sin(angle + share / 2) })
      angle += share
    }

    at += ring.length
    radius = r
    prevExtent = ringExtent
  }

  return { positions, decorations: [] }
}

/* ------------------------------------------------------------------------ */
/* Legatura cu cytoscape                                                     */
/* ------------------------------------------------------------------------ */

/**
 * Ce asezare se aplica de fapt. `auto` pastreaza ce era pe fiecare vedere -
 * ierarhie cu sens pe infrastructura, ierarhie fara sens pe topologie, forta
 * pe comunicatie - numai ca fara suprapuneri.
 */
export function resolveArrange(view: string, arrange: string): Exclude<Arrange, 'auto'> | 'hierarchy-directed' {
  const chosen = isArrange(arrange) ? arrange : 'auto'
  if (chosen !== 'auto') return chosen
  if (view === 'infrastructure') return 'hierarchy-directed'
  if (view === 'topology') return 'hierarchy'
  return 'force'
}

/**
 * Optiunile date lui cytoscape. Pe toate, in afara de forta, pozitiile le
 * pune `arrangeMap()` - cytoscape primeste `preset`. Forta e fCoSE: fara
 * animatie (o harta care danseaza doua secunde nu se citeste), cu etichetele
 * socotite in marimea nodului, si cu spatiul crescut odata cu numarul de
 * noduri.
 */
export function layoutOptions(view: string, count: number, arrange: string = 'auto'): Record<string, unknown> {
  if (resolveArrange(view, arrange) !== 'force') return { name: 'preset', fit: false }

  const scale = count > 80 ? 1.6 : count > 40 ? 1.35 : 1.15
  return {
    name: 'fcose',
    quality: 'default',
    randomize: true,
    animate: false,
    fit: false,
    padding: 30,
    nodeDimensionsIncludeLabels: true,
    uniformNodeDimensions: false,
    /* Insulele le asaza `packComponents()` de aici, nu extensia
       layout-utilities, pe care n-o aducem. */
    packComponents: false,
    nodeSeparation: 90,
    nodeRepulsion: () => 9000 * scale,
    idealEdgeLength: () => 90 * scale,
    edgeElasticity: () => 0.45,
    gravity: 0.25,
    numIter: 2500,
    tile: true,
  }
}

/* Cat ii trebuie lui `arrangeMap` din cytoscape - restul nu-l atinge. */
type ArrangeNode = {
  id: () => string
  data: (key?: string) => unknown
  position: ((p?: { x: number; y: number }) => { x: number; y: number }) & ((p: { x: number; y: number }) => void)
  boundingBox: (o: Record<string, boolean>) => { x1: number; y1: number; w: number; h: number }
  isParent?: () => boolean
  hasClass: (c: string) => boolean
}

type ArrangeCollection = {
  length: number
  forEach: (fn: (el: ArrangeNode) => void) => void
  remove: () => void
}

export type ArrangeCy = {
  nodes: (selector?: string) => ArrangeCollection
  edges: (selector?: string) => { forEach: (fn: (el: { data: (key?: string) => unknown }) => void) => void }
  batch: (fn: () => void) => void
  add: (els: Record<string, unknown>[]) => unknown
  layout: (opts: Record<string, unknown>) => { run: () => void }
  fit: (eles: unknown, padding: number) => void
  zoom: ((level?: number | { level: number; renderedPosition: { x: number; y: number } }) => number) & ((o: unknown) => void)
  center: () => void
  width: () => number
  height: () => number
}

let registered: unknown = null

/** fCoSE, inregistrat o singura data pe biblioteca portalului. */
export function registerLayouts(cytoscape: unknown): void {
  if (registered === cytoscape || typeof cytoscape !== 'function') return
  const use = (cytoscape as { use?: (ext: unknown) => void }).use
  if (typeof use !== 'function') return
  try {
    use.call(cytoscape, fcose)
  } catch {
    /* Deja inregistrat (alt ecran l-a pus) - e acelasi lucru. */
  }
  registered = cytoscape
}

/** Benzile si blocurile: sub tot, fara clic, cu numele lor subtire. */
export function arrangeStyle(): object[] {
  return [
    {
      selector: 'node.arrange-deco',
      style: {
        shape: 'round-rectangle',
        width: 'data(w)',
        height: 'data(h)',
        label: 'data(label)',
        'background-color': '#3598dc',
        'background-opacity': 0.035,
        'border-width': 1,
        'border-color': '#e1e8ee',
        'border-opacity': 1,
        color: '#9aa6b2',
        'font-size': '11px',
        'font-weight': 600,
        'text-background-opacity': 0,
        'text-margin-y': 0,
        'z-compound-depth': 'bottom',
        'z-index': 0,
        events: 'no',
        'overlay-opacity': 0,
      },
    },
    {
      selector: 'node.arrange-band',
      style: { 'text-halign': 'left', 'text-valign': 'center', 'text-margin-x': -10, 'text-wrap': 'wrap', 'text-max-width': '140px' },
    },
    {
      selector: 'node.arrange-group',
      style: { 'text-halign': 'center', 'text-valign': 'top', 'text-margin-y': -4, 'background-opacity': 0.02 },
    },
  ]
}

export interface ArrangeRequest {
  view: string
  arrange: string
  root: number | string | null
}

/**
 * Aseaza harta deja desenata. Nu reincarca nimic: ia nodurile si legaturile
 * din panza, le masoara cu eticheta, calculeaza, si muta.
 */
export function arrangeMap(cy: ArrangeCy, request: ArrangeRequest): void {
  cy.nodes('.arrange-deco').remove()

  const mode = resolveArrange(request.view, request.arrange)
  const nodes: TierNode[] = []
  const live = new Map<string, ArrangeNode>()
  const sizes = new Map<string, Size>()
  /* Centrul dreptunghiului (cu eticheta sub nod) nu e centrul nodului. */
  const offsets = new Map<string, { dx: number; dy: number }>()
  /* Membru -> caseta (nodul parinte de la grupare). Gol = harta fara casete. */
  const parentOf = new Map<string, string>()

  const measure = (n: ArrangeNode) => {
    const box = n.boundingBox({ includeLabels: true, includeOverlays: false, includeUnderlays: false, includeEdges: false })
    const p = n.position()
    sizes.set(n.id(), { w: box.w, h: box.h })
    offsets.set(n.id(), { dx: box.x1 + box.w / 2 - p.x, dy: box.y1 + box.h / 2 - p.y })
  }

  cy.nodes().forEach((n) => {
    if (n.hasClass('arrange-deco') || (n.isParent && n.isParent())) return
    const data = n.data() as TierNode & { id: string; parent?: string }
    nodes.push({ ...data, id: n.id() })
    if (data.parent !== undefined && data.parent !== null && data.parent !== '') parentOf.set(n.id(), String(data.parent))
    live.set(n.id(), n)
    measure(n)
  })

  const edges: TierEdge[] = []
  cy.edges().forEach((e) => {
    const d = e.data() as TierEdge
    edges.push({ source: String(d.source), target: String(d.target), type: d.type, port: d.port, label: d.label })
  })

  /* Pozitiile date de forta (fCoSE stie de casete: le tine membrii laolalta). */
  const forcePositions = (): Map<string, { x: number; y: number }> => {
    if (nodes.length > 1) cy.layout(layoutOptions(request.view, nodes.length, 'force')).run()
    const positions = new Map<string, { x: number; y: number }>()
    live.forEach((n, id) => {
      const p = n.position()
      const o = offsets.get(id)!
      positions.set(id, { x: p.x + o.dx, y: p.y + o.dy })
    })
    return positions
  }

  const arrangeOne = (part: TierNode[], partEdges: TierEdge[]): Arrangement => {
    const ids = new Set(part.map((n) => String(n.id)))
    const root = request.root !== null && request.root !== undefined && ids.has(String(request.root)) ? request.root : null
    if (mode === 'tiers') return tiersArrangement(part, partEdges, sizes)
    if (mode === 'grid') return gridArrangement(part, partEdges, sizes)
    if (mode === 'concentric') return concentricArrangement(part, partEdges, sizes, root)
    return hierarchyArrangement(part, partEdges, sizes, root, mode === 'hierarchy-directed')
  }

  let arrangement: Arrangement
  let finalRects: Rect[]
  if (parentOf.size > 0) {
    /* CASETELE (gruparea pe sediu / retea / aplicatie) - fiecare caseta se
       aseaza singura, cu asezarea aleasa, si apoi casetele se pun una langa
       alta. Altfel benzile ar imprastia membrii unei casete pe toata harta, iar
       caseta - care e doar conturul copiilor ei - ar acoperi tot. */
    const positions = groupedArrangement(
      nodes,
      edges,
      sizes,
      parentOf,
      (part, partEdges) => arrangeOne(part, partEdges).positions,
      mode === 'force' ? forcePositions() : undefined,
    )
    arrangement = { positions, decorations: [] }
    /* Neacoperirea e garantata deja, in caseta si intre casete; un pas global
       ar putea muta un membru afara din caseta lui. */
    finalRects = toRects(positions, sizes)
  } else {
    if (mode === 'force') {
      const rects = packComponents(toRects(forcePositions(), sizes), edges)
      arrangement = { positions: new Map(rects.map((r) => [r.id, { x: r.x, y: r.y }])), decorations: [] }
    } else {
      arrangement = arrangeOne(nodes, edges)
    }

    /* Pasul final, pe toate: pe benzi si pe grila n-are ce face, pe forta si pe
       inele e garantia. */
    finalRects = removeOverlaps(toRects(arrangement.positions, sizes))
  }

  cy.batch(() => {
    for (const r of finalRects) {
      const o = offsets.get(r.id)!
      live.get(r.id)?.position({ x: r.x - o.dx, y: r.y - o.dy })
    }
  })

  if (arrangement.decorations.length > 0) {
    cy.add(
      arrangement.decorations.map((d) => ({
        group: 'nodes',
        data: { id: d.id, label: d.label, w: d.w, h: d.h },
        position: { x: d.x, y: d.y },
        classes: `arrange-deco arrange-${d.kind}`,
        selectable: false,
        grabbable: false,
        locked: true,
      })),
    )
  }
}

/** Cat adauga conturul unei casete in jurul membrilor: `padding` 18 si numele sus. */
export const BOX_PAD = { side: 20, top: 42, bottom: 20 }
/** Spatiul dintre doua casete. */
export const BOX_GAP = 40

/**
 * ASEZAREA CU CASETE. Fiecare caseta (si, separat, nodurile din afara
 * casetelor) se aseaza singura cu `arrangeOne`; apoi casetele, ca dreptunghiuri
 * intregi, se pun pe randuri in ordinea lor. La forta (`force` dat), pozitiile
 * vin din fCoSE: in caseta doar se scot suprapunerile, iar casetele se imping
 * una de alta pastrand locul ales de forta.
 *
 * Intoarce centrul dreptunghiului fiecarui nod, ca celelalte asezari.
 */
export function groupedArrangement(
  nodes: TierNode[],
  edges: TierEdge[],
  sizes: Map<string, Size>,
  parentOf: Map<string, string>,
  arrangeOne: (nodes: TierNode[], edges: TierEdge[]) => Map<string, { x: number; y: number }>,
  force?: Map<string, { x: number; y: number }>,
): Map<string, { x: number; y: number }> {
  const buckets = new Map<string, TierNode[]>()
  for (const n of nodes) {
    const key = parentOf.get(String(n.id)) ?? ''
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key)!.push(n)
  }
  /* Casetele intai, in ordinea lor; ce sta in afara lor, la urma. */
  const loose = buckets.get('')
  buckets.delete('')

  type Block = { members: Rect[]; left: number; top: number; w: number; h: number }
  const blocks: Block[] = []
  const block = (members: Rect[], boxed: boolean) => {
    if (members.length === 0) return
    const pad = boxed ? BOX_PAD : { side: 0, top: 0, bottom: 0 }
    const left = Math.min(...members.map((r) => r.x - r.w / 2)) - pad.side
    const right = Math.max(...members.map((r) => r.x + r.w / 2)) + pad.side
    const top = Math.min(...members.map((r) => r.y - r.h / 2)) - pad.top
    const bottom = Math.max(...members.map((r) => r.y + r.h / 2)) + pad.bottom
    blocks.push({ members, left, top, w: right - left, h: bottom - top })
  }
  const place = (part: TierNode[]): Rect[] => {
    const ids = new Set(part.map((n) => String(n.id)))
    const partEdges = edges.filter((e) => ids.has(String(e.source)) && ids.has(String(e.target)))
    const positions = force
      ? new Map([...ids].filter((id) => force.has(id)).map((id) => [id, force.get(id)!]))
      : arrangeOne(part, partEdges)
    return removeOverlaps(toRects(positions, sizes))
  }

  buckets.forEach((part) => block(place(part), true))
  if (loose) {
    if (force) for (const r of place(loose)) block([r], false)
    else block(place(loose), false)
  }

  const out = new Map<string, { x: number; y: number }>()
  const move = (b: Block, dx: number, dy: number) => {
    for (const r of b.members) out.set(r.id, { x: r.x + dx, y: r.y + dy })
  }

  if (force) {
    const rects = blocks.map((b, i) => ({ id: String(i), x: b.left + b.w / 2, y: b.top + b.h / 2, w: b.w, h: b.h }))
    const moved = new Map(removeOverlaps(rects, BOX_GAP).map((r) => [r.id, r]))
    blocks.forEach((b, i) => {
      const m = moved.get(String(i))!
      move(b, m.x - rects[i].x, m.y - rects[i].y)
    })
    return out
  }

  /* Pe randuri, cat sa iasa cam de doua ori mai lat decat inalt. */
  const area = blocks.reduce((s, b) => s + (b.w + BOX_GAP) * (b.h + BOX_GAP), 0)
  const maxWidth = Math.max(...blocks.map((b) => b.w), Math.sqrt(area) * 1.6)
  let x = 0
  let y = 0
  let rowHeight = 0
  for (const b of blocks) {
    if (x > 0 && x + b.w > maxWidth) {
      x = 0
      y += rowHeight + BOX_GAP
      rowHeight = 0
    }
    move(b, x - b.left, y - b.top)
    x += b.w + BOX_GAP
    rowHeight = Math.max(rowHeight, b.h)
  }
  return out
}

function toRects(positions: Map<string, { x: number; y: number }>, sizes: Map<string, Size>): Rect[] {
  const out: Rect[] = []
  positions.forEach((p, id) => {
    const z = sizes.get(id)
    if (z) out.push({ id, x: p.x, y: p.y, w: z.w, h: z.h })
  })
  return out
}

/** Reasezare din selector: aceeasi panza, alta asezare, apoi incadrata din nou. */
export function rearrangeMap(cy: ArrangeCy, request: ArrangeRequest): void {
  arrangeMap(cy, request)
  cy.fit(null, 25)
  if (cy.zoom() > 1.4) {
    cy.zoom({ level: 1.4, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
    cy.center()
  }
}
